import React, {
  FC,
  InputHTMLAttributes,
  ReactNode,
  TextareaHTMLAttributes,
} from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import {Icon} from './Icon';
import {cx} from './cx';

interface FieldProps {
  label: string;
  htmlFor: string;
  hint?: string;
  error?: string;
  optional?: boolean;
  children: ReactNode;
  className?: string;
}

export const Field: FC<FieldProps> = ({
  label,
  htmlFor,
  hint,
  error,
  optional,
  children,
  className,
}) => (
  <div className={cx('sui-field', error && 'has-error', className)}>
    <div className="sui-field__label-row">
      <label htmlFor={htmlFor}>{label}</label>
      {optional && <span>Optional</span>}
    </div>
    {children}
    {(error || hint) && (
      <small className="sui-field__message">{error || hint}</small>
    )}
  </div>
);

export const Input: FC<InputHTMLAttributes<HTMLInputElement>> = ({
  className,
  ...props
}) => <input className={cx('sui-input', className)} {...props} />;

export const Textarea: FC<TextareaHTMLAttributes<HTMLTextAreaElement>> = ({
  className,
  ...props
}) => <textarea className={cx('sui-input sui-textarea', className)} {...props} />;

export interface SelectOption {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
}

interface SelectProps {
  id?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  name?: string;
  value?: string;
  defaultValue?: string;
  placeholder?: string;
  options: SelectOption[];
  onValueChange?: (value: string) => void;
  disabled?: boolean;
  required?: boolean;
  className?: string;
}

export const Select: FC<SelectProps> = ({
  id,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  name,
  value,
  defaultValue,
  placeholder = 'Choose an option',
  options,
  onValueChange,
  disabled,
  required,
  className,
}) => (
  <SelectPrimitive.Root
    defaultValue={defaultValue}
    disabled={disabled}
    name={name}
    onValueChange={onValueChange}
    required={required}
    value={value}
  >
    <SelectPrimitive.Trigger
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      className={cx('sui-input sui-select-trigger', className)}
      id={id}
    >
      <SelectPrimitive.Value placeholder={placeholder} />
      <SelectPrimitive.Icon className="sui-select-trigger__icon">
        <Icon name="chevron-down" size={17} />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        className="sui-select-content"
      >
        <SelectPrimitive.ScrollUpButton className="sui-select-scroll">
          <Icon
            className="sui-select-scroll__up"
            name="chevron-down"
            size={17}
          />
        </SelectPrimitive.ScrollUpButton>
        <SelectPrimitive.Viewport className="sui-select-viewport">
          {options.map(option => (
            <SelectPrimitive.Item
              className="sui-select-item"
              disabled={option.disabled}
              key={option.value}
              value={option.value}
            >
              <SelectPrimitive.ItemIndicator className="sui-select-item__indicator">
                <Icon name="check" size={16} />
              </SelectPrimitive.ItemIndicator>
              <SelectPrimitive.ItemText>
                <span className="sui-select-item__label">{option.label}</span>
              </SelectPrimitive.ItemText>
              {option.description && (
                <span className="sui-select-item__description">
                  {option.description}
                </span>
              )}
            </SelectPrimitive.Item>
          ))}
        </SelectPrimitive.Viewport>
        <SelectPrimitive.ScrollDownButton className="sui-select-scroll">
          <Icon name="chevron-down" size={17} />
        </SelectPrimitive.ScrollDownButton>
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  </SelectPrimitive.Root>
);

interface CheckboxProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  description?: string;
}

export const Checkbox: FC<CheckboxProps> = ({
  label,
  description,
  className,
  ...props
}) => (
  <label className={cx('sui-checkbox', className)}>
    <input type="checkbox" {...props} />
    <span className="sui-checkbox__mark" />
    <span>
      <strong>{label}</strong>
      {description && <small>{description}</small>}
    </span>
  </label>
);

export interface SegmentOption {
  value: string;
  label: string;
  description?: string;
}

interface SegmentedControlProps {
  label: string;
  options: SegmentOption[];
  value: string;
  onChange: (value: string) => void;
  className?: string;
}

export const SegmentedControl: FC<SegmentedControlProps> = ({
  label,
  options,
  value,
  onChange,
  className,
}) => (
  <div
    aria-label={label}
    className={cx('sui-segmented', className)}
    role="radiogroup"
    onKeyDown={event => {
      if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
      const radios = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="radio"]'));
      const current = (event.target as HTMLElement).closest('[role="radio"]') as HTMLButtonElement;
      const index = radios.indexOf(current);
      if (index < 0 || !options.length) return;
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? options.length - 1
        : (index + (event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 1) + options.length) % options.length;
      event.preventDefault();
      onChange(options[next].value);
      radios[next].focus();
    }}
  >
    {options.map((option, index) => (
      <button
        aria-checked={option.value === value}
        className={cx(
          'sui-segmented__option',
          option.value === value && 'is-selected'
        )}
        key={option.value}
        onClick={() => onChange(option.value)}
        role="radio"
        tabIndex={option.value === value || (!options.some(item => item.value === value) && index === 0) ? 0 : -1}
        type="button"
      >
        <strong>{option.label}</strong>
        {option.description && <small>{option.description}</small>}
      </button>
    ))}
  </div>
);
